/**
 * GLAMRDiP /cart page — qty + remove handlers.
 * Joel 2026-04-30. Mirrors the cart-drawer's plumbing but reloads the
 * page (rather than re-rendering DOM) since the cart page is server-
 * rendered Liquid. Kit removal cascades to all linked addon lines via
 * /cart/update.js, the same way cart-drawer.js does it.
 */
(() => {
  'use strict';
  const root = document.querySelector('[data-gd-cart-page-items]');
  if (!root) return;

  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      return r.ok ? r.json() : null;
    } catch { return null; }
  }
  async function changeLine(line, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity: qty })
    });
  }
  async function updateLines(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
  }

  // Joel 2026-05-02: defensive guard for /cart/change.js + /cart/update.js
  // calls on system-managed lines. Layer 1 (gd-cart-items.liquid) hides
  // the qty stepper + remove button for free-gift lines, so under normal
  // UI flow this guard never fires. It's belt-and-suspenders for DOM
  // tampering (DevTools-injected buttons) and any future surface that
  // might wire directly to changeLine() without re-checking properties.
  // Final defense lives in gd-cart-sanitizer.js Layer 3.
  async function isLockedLine(line) {
    try {
      const cart = await fetchCart();
      if (!cart) return false;
      const item = (cart.items || [])[line - 1];
      if (!item) return false;
      const props = item.properties || {};
      return props._free_gift === 'true' || props._addon === 'true';
    } catch {
      return false;
    }
  }

  // --- Qty stepper -----------------------------------------------------
  root.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-gd-cart-page-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const dir = qtyBtn.dataset.gdCartPageQty;
      const line = parseInt(qtyBtn.dataset.line, 10);
      const valEl = root.querySelector(`.gd-cart-page__qty-val[data-line="${line}"]`);
      const cur = valEl ? parseInt(valEl.textContent, 10) : 1;
      const next = dir === 'inc' ? cur + 1 : Math.max(0, cur - 1);
      qtyBtn.disabled = true;
      try {
        if (await isLockedLine(line)) {
          console.warn('[gd-cart-page] qty change blocked on system-managed line', line);
          qtyBtn.disabled = false;
          return;
        }
        await changeLine(line, next);
        // Reload — server-rendered Liquid is the source of truth for the
        // cart page (totals, savings, sticky-summary numbers all need a
        // fresh server render).
        window.location.reload();
      } catch {
        qtyBtn.disabled = false;
      }
      return;
    }

    // --- Remove --------------------------------------------------------
    const removeBtn = e.target.closest('[data-gd-cart-page-remove]');
    if (removeBtn) {
      e.preventDefault();
      const item = removeBtn.closest('[data-gd-cart-page-item]');
      removeBtn.setAttribute('aria-busy', 'true');
      if (item) item.dataset.removing = 'true';

      const line = parseInt(removeBtn.dataset.line, 10);
      const isKit = removeBtn.dataset.isKit === 'true';
      const bundleId = removeBtn.dataset.bundleId || '';

      try {
        // Defensive guard — same rationale as qty path above.
        if (await isLockedLine(line)) {
          console.warn('[gd-cart-page] remove blocked on system-managed line', line);
          removeBtn.removeAttribute('aria-busy');
          if (item) delete item.dataset.removing;
          return;
        }
        if (isKit && bundleId) {
          // Cascade: zero parent + every addon sharing this bundle_id.
          const cart = await fetchCart();
          if (cart) {
            const total = cart.items.length;
            const updates = new Array(total);
            for (let i = 0; i < total; i++) updates[i] = cart.items[i].quantity;
            cart.items.forEach((it, i) => {
              const props = it.properties || {};
              const matches = (i + 1 === line) ||
                              (props._addon === 'true' && props.__bundle_id === bundleId);
              if (matches) updates[i] = 0;
            });
            await updateLines(updates);
          } else {
            await changeLine(line, 0);
          }
        } else {
          await changeLine(line, 0);
        }
        window.location.reload();
      } catch {
        removeBtn.removeAttribute('aria-busy');
        if (item) delete item.dataset.removing;
      }
    }
  });
})();
